/**
 * New-diagram form validation
 *
 * The pure input checks for the New diagram dialog (task 5.3): the Diagram_Title
 * and the optional Diagram_Folder. Kept out of the dialog component so they can
 * be unit-tested without rendering, and so the dialog and any future importer
 * (task 5.5) share exactly one definition of what a valid folder is.
 *
 * These mirror the backend's trust-boundary checks (Requirement 4.1 and the
 * Glossary): a title is 1–200 characters; a folder, when given, is up to four
 * Folder_Segments joined by "/", each a lowercase kebab-case slug of at most
 * 40 characters (for example `data/routine-surveillance`). The dialog validates
 * here before calling the API so the user sees an inline error rather than a
 * 400 from the server.
 */

/** The maximum Diagram_Title length, in characters (Requirement 3.3). */
export const MAX_TITLE_LENGTH: number = 200

/** The most Folder_Segments a Diagram_Folder may have (backend FOLDER_MAX_DEPTH). */
export const MAX_FOLDER_DEPTH: number = 4

/** The maximum length of one Folder_Segment, in characters (backend FOLDER_SEGMENT_MAX_LENGTH). */
export const MAX_FOLDER_SEGMENT_LENGTH: number = 40

/**
 * A single Folder_Segment: one or more hyphen-separated groups of lowercase
 * ASCII letters and digits, with no leading, trailing, or doubled hyphens.
 * Matches the backend's `FOLDER_SEGMENT_PATTERN`.
 */
const FOLDER_SEGMENT_PATTERN: RegExp = /^[a-z0-9]+(-[a-z0-9]+)*$/

/**
 * Validate a Diagram_Title.
 *
 * A title is required and, once trimmed, must be 1–200 characters. Leading and
 * trailing whitespace is ignored for the length check (and trimmed before the
 * title is used).
 *
 * @param title - The raw title as typed by the user.
 * @returns An error message to show inline, or null when the title is valid.
 */
export function validateTitle(title: string): string | null {
  const trimmed: string = title.trim()
  if (trimmed === "") {
    return "A title is required."
  }
  if (trimmed.length > MAX_TITLE_LENGTH) {
    return `A title can be at most ${MAX_TITLE_LENGTH} characters.`
  }
  return null
}

/**
 * Validate an optional Diagram_Folder.
 *
 * An empty folder is valid (the diagram is unfiled). A non-empty folder is up
 * to MAX_FOLDER_DEPTH Folder_Segments joined by "/", where each segment is
 * kebab-case (lowercase letters, digits, and single hyphens between them) and
 * at most MAX_FOLDER_SEGMENT_LENGTH characters. The folder is not trimmed: a
 * surrounding space makes it invalid rather than being silently stripped, so
 * the user sees exactly what the path will be built from. An empty segment
 * (a leading, trailing, or doubled "/") fails the segment pattern. This mirrors
 * the backend's `_folder_segments` rule exactly (Requirement 4.1, 4.2).
 *
 * @param folder - The raw folder as chosen or typed by the user.
 * @returns An error message to show inline, or null when the folder is valid.
 */
export function validateFolder(folder: string): string | null {
  if (folder === "") {
    return null
  }
  const invalidMessage: string =
    `A folder is up to ${MAX_FOLDER_DEPTH} levels separated by /, each made of ` +
    `lowercase letters, digits, and hyphens (at most ${MAX_FOLDER_SEGMENT_LENGTH} ` +
    "characters), e.g. data/routine-surveillance."
  const segments: string[] = folder.split("/")
  if (segments.length > MAX_FOLDER_DEPTH) {
    return invalidMessage
  }
  for (const segment of segments) {
    if (segment.length > MAX_FOLDER_SEGMENT_LENGTH || !FOLDER_SEGMENT_PATTERN.test(segment)) {
      return invalidMessage
    }
  }
  return null
}
